import type { CalendarDate } from '@internationalized/date';
import { parseDateLoosely } from '@/utils/time';
import { type BillingCadence, isNonMonthlyContract, ordwayCadence } from './cadence';
import { baseOrdwayProductIds } from './ordway/products';
import { startupCohort } from './startup-coupons';
import { BILLING_TIME_ZONE } from './time-zone';
import type { OrdwaySubscriptionBillingPlan } from './transforms';

/** When a scheduled plan change takes effect, relative to the current billing cycle. */
export const PlanChangeTiming = {
  /** The new plan starts on the day the current cycle resets. */
  EndOfCycle: 'endOfCycle',
  /** The new plan starts before the cycle resets, so the cycle can be cut short or move. */
  MidCycle: 'midCycle',
  /** The new plan starts after the cycle resets, so the next charge is the current plan's. */
  AfterNextCharge: 'afterNextCharge',
} as const;
export type PlanChangeTiming = (typeof PlanChangeTiming)[keyof typeof PlanChangeTiming];

export type ScheduledPlanChange = {
  plan: OrdwaySubscriptionBillingPlan;
  effectiveAt: number;
  /** The base price of the new plan, in cents. */
  basePriceCents: number;
  timing: PlanChangeTiming;
  cadence: BillingCadence;
  /**
   * True when the next charge carries the base price of the new plan. Only a monthly base is charged
   * on the cycle, and a lump sum is invoiced apart from it.
   */
  isBaseOnNextCharge: boolean;
};

function baseCadence(plan: OrdwaySubscriptionBillingPlan): BillingCadence {
  const base = plan.plans?.find((product) => baseOrdwayProductIds().includes(product.product_id));
  return ordwayCadence(base?.billing_period);
}

function timingFor(startsOn: CalendarDate, resetsOn: CalendarDate | undefined): PlanChangeTiming {
  // Without a reset, the change cannot be shown to wait for the cycle, so it counts as mid cycle.
  if (resetsOn == null) {
    return PlanChangeTiming.MidCycle;
  }

  const order = startsOn.compare(resetsOn);
  if (order < 0) {
    return PlanChangeTiming.MidCycle;
  }
  if (order === 0) {
    return PlanChangeTiming.EndOfCycle;
  }
  return PlanChangeTiming.AfterNextCharge;
}

/**
 * The plan an Ordway account moves onto, and when.
 *
 * Ordway sends calendar dates, so the start of the new plan and the reset of the current cycle are
 * compared as days.
 */
export function scheduledPlanChange(
  current: OrdwaySubscriptionBillingPlan,
  upcoming: OrdwaySubscriptionBillingPlan,
): ScheduledPlanChange | undefined {
  const startsOn = parseDateLoosely(upcoming.billing_start_date ?? '');
  if (startsOn == null) {
    return undefined;
  }

  const timing = timingFor(startsOn, parseDateLoosely(current.usage_reset_date ?? ''));
  const cadence = baseCadence(upcoming);

  // A Startup-program plan states $0 and is billed the price of its cohort, as the current plan is.
  const cohort = startupCohort(undefined, upcoming);

  return {
    plan: upcoming,
    basePriceCents: cohort?.monthlyPriceCents ?? upcoming.basePrice,
    effectiveAt: Math.floor(startsOn.toDate(BILLING_TIME_ZONE).getTime() / 1000),
    timing,
    cadence,
    isBaseOnNextCharge: timing === PlanChangeTiming.EndOfCycle && !upcoming.isLumpSum && !isNonMonthlyContract(cadence),
  };
}
